import React from "react";
import classNames from "classnames";
import { useForm, RadioButton, Slider } from "@netapp/bxp-design-system-react"
import styles from "./CapacitySettings.module.scss";
import { OptionalGroupButton } from "modules/connectors/components/OptionalGroup";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { connectorsettings as i18nNamespace } from "../../../../i18n/namespace";

const infoText = (t) => {
    return {
        capacityManagement: t("capacity_management_text"),
        aggregateCapacity: <div>{t("aggregate_capacity_info")}
            <div>{t("free_space_ratio")}</div>
            <div className={classNames(styles['equation'])}>
                <div className={classNames(styles['division'])}>{t("ratio_formula")}
                </div>
                {t("aggregate_capacity")}
            </div>
        </div>,
        aggregateCapacityTier: t("aggregate_capacity_tier"),
        VolumeAutosize: t("volumne_autosize")
    }
}

const CapacityManagementTitle = () => {
    const configValue = useSelector(state => state.connectorSettings.settings.config.autoVsaCapacityManagement);
    const { t } = useTranslation(i18nNamespace);
    return {
        title: t("capacity_management_mode"),
        value: configValue ? t("automatic_mode") : t("manual_mode"),
        tooltip: infoText(t).capacityManagement
    }
}

const CapacityManagementContent = ({ close: closeOptionalGroup }) => {
    const configValue = useSelector(state => state.connectorSettings.settings.config.autoVsaCapacityManagement);
    const dispatch = useDispatch();
    const form = useForm({ automaticMode: configValue ? 'yes' : 'no' });
    const { formState } = form;
    const configHasChanged = (formState, configValue) => !(formState.automaticMode === (configValue ? 'yes' : 'no'));
    const { t } = useTranslation(i18nNamespace);
    const save = () => {
        dispatch({
            type: "CURRENT-CONNECTOR:UPDATE-CONFIG",
            payload: {
                autoVsaCapacityManagement: formState.automaticMode === 'yes' ? true : false
            }
        });
        closeOptionalGroup();
    };
    return <div className={classNames(styles['capacity-content-container'])}>
        <div className={classNames(styles['capacity-content'])}>
            <div>
                <RadioButton form={form} name="automaticMode" value='no'>{t("manual_mode")}</RadioButton>
                <p>{t("manual_mode_desc")}</p>
            </div>
            <div className={classNames(styles['second-radio'])}>
                <RadioButton form={form} name="automaticMode" value='yes'>{t("automatic_mode")}</RadioButton>
                <p>{t("automatic_mode_desc")}</p>
            </div>
        </div>
        <OptionalGroupButton disabled={!configHasChanged(form.formState, configValue)} cancel={closeOptionalGroup} save={save} />
    </div>
}

const AggregateCapacityTitle = () => {
    const configValue = useSelector(state => state.connectorSettings.settings.config.maxAggregateFreeSpacePercentage);
    const { t } = useTranslation(i18nNamespace);
    return {
        title: t("aggregate_capacity_thresholds"),
        value: `${configValue}%`,
        tooltip: infoText(t).aggregateCapacity
    }
}

const AggregateCapacityContent = ({ close: closeOptionalGroup }) => {
    return <SliderOptionalGroup max={50} min={1} closeOptionalGroup={closeOptionalGroup}
        configKey={'maxAggregateFreeSpacePercentage'} />
}

const AggregateCapacityDataTieringTitle = () => {
    const { t } = useTranslation(i18nNamespace);
    const configValue = useSelector(state => state.connectorSettings.settings.config.s3EbsRatio);
    return {
        title: t("aggreate_capacity_thresholds_data_tiering"),
        value: `${configValue}%`,
        tooltip: infoText(t).aggregateCapacityTier
    }
}
const AggregateCapacityDataTieringContent = ({ close: closeOptionalGroup }) => {
    return <SliderOptionalGroup closeOptionalGroup={closeOptionalGroup} configKey={'s3EbsRatio'} />
}

const VolumeAutosizeTitle = () => {
    const configValue = useSelector(state => state.connectorSettings.settings.config.maxVolumeGrowSizePercentage);
    const { t } = useTranslation(i18nNamespace);
    return {
        title: t("volume_autosize"),
        value: `${configValue}%`,
        tooltip: infoText(t).VolumeAutosize
    }
}

const VolumeAutosizeContent = ({ close: closeOptionalGroup }) => {
    const { t } = useTranslation(i18nNamespace);
    return <SliderOptionalGroup max={1000} closeOptionalGroup={closeOptionalGroup}
        configKey={'maxVolumeGrowSizePercentage'}
        notification={t("changes_affect_volumes")} />
}


const SliderOptionalGroup = ({ closeOptionalGroup, configKey, min = 0, max = 100, notification = null }) => {
    const configValue = useSelector(state => state.connectorSettings.settings.config[configKey]);
    const dispatch = useDispatch();
    const form = useForm({ formValue: configValue });
    const { formState, handleFormChange } = form;
    const { t } = useTranslation(i18nNamespace);


    const configHasChanged = (formState, configValue) => !(formState.formValue === configValue);
    const save = () => {
        const newConfig = Object.fromEntries([[configKey, formState.formValue]]);
        dispatch({
            type: "CURRENT-CONNECTOR:UPDATE-CONFIG",
            payload: {
                ...newConfig
            }
        });
        closeOptionalGroup();
    };
    const Notice = () => {
        return notification && <div className={classNames(styles['slider-notification'])}>
            <p><span>{t("notice")} </span>{notification}</p>
        </div>
    }
    return <>
        <div className={classNames(styles['slider'])}>
            <Slider max={max} min={min} form={form} name={"formValue"} value={formState.formValue}
                onChange={(e) => handleFormChange(e, { name: "formValue" })} unitSuffix={"%"} />
            <Notice />
        </div>
        <OptionalGroupButton disabled={!configHasChanged(form.formState, configValue)} cancel={closeOptionalGroup} save={save} />
    </>
}

export const CapacityManagement = {
    Title: CapacityManagementTitle,
    Content: CapacityManagementContent
}
export const AggregateCapacity = {
    Title: AggregateCapacityTitle,
    Content: AggregateCapacityContent
}

export const AggregateCapacityDataTiering = {
    Title: AggregateCapacityDataTieringTitle,
    Content: AggregateCapacityDataTieringContent
}

export const VolumeAutosize = {
    Title: VolumeAutosizeTitle,
    Content: VolumeAutosizeContent
}